"use client";

import { useEffect, useMemo, useRef, useState } from "react";

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";

import {
  groupVariablesByCategory,
  VARIABLE_CATEGORY_LABELS,
  VARIABLE_CATEGORY_ORDER,
  type VariableDefinition,
} from "../../lib/variable-catalog";

export interface VariableMenuProps {
  /**
   * Fired when the caller (toolbar button or slash-command suggestion plugin)
   * selects a variable. Receives the catalog definition; the caller maps that
   * to the `variable-mention` node attrs.
   */
  onInsert: (definition: VariableDefinition) => void;
  /** Fired on Escape inside the search input. */
  onClose: () => void;
  /** Optional className applied to the outer Command root for sizing. */
  className?: string;
  /** Auto-focus the search input on mount. Default `true` so popovers feel snappy. */
  autoFocus?: boolean;
}

/**
 * Slash-command popover for inserting a TipTap `variable-mention` node.
 *
 * Wraps the shared `Command` (cmdk) primitive so it inherits keyboard nav and
 * fuzzy filtering for free. Items expose both their `label` and `key` to
 * cmdk's filter so users can type either.
 *
 * Spec: §5.2 (slash-command popover) and §5.6 (V1 catalog).
 */
export function VariableMenu({
  onInsert,
  onClose,
  className,
  autoFocus = true,
}: VariableMenuProps) {
  const grouped = useMemo(() => groupVariablesByCategory(), []);
  const [search, setSearch] = useState("");
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus]);

  return (
    <Command
      label="Insert variable"
      loop
      className={cn(
        "w-[320px] overflow-hidden rounded-lg border border-contrast/1 bg-surface-elevated shadow-[0px_8px_24px_0px_rgba(0,0,0,0.35)]",
        className,
      )}
    >
      <CommandInput
        ref={inputRef}
        placeholder="Search variables…"
        value={search}
        onValueChange={setSearch}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            event.preventDefault();
            onClose();
          }
        }}
      />
      <CommandList>
        <CommandEmpty>No matching variables</CommandEmpty>
        {VARIABLE_CATEGORY_ORDER.map((category) => {
          const items = grouped[category];
          if (items.length === 0) return null;
          return (
            <CommandGroup
              key={category}
              heading={VARIABLE_CATEGORY_LABELS[category]}
              className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:pt-2 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wide [&_[cmdk-group-heading]]:text-contrast/40"
            >
              {items.map((definition) => (
                <CommandItem
                  key={definition.key}
                  value={`${definition.label} ${definition.key}`}
                  onSelect={() => onInsert(definition)}
                  className="flex flex-col items-start gap-0.5 rounded-md px-2.5 py-1.5"
                >
                  <span className="text-sm font-medium text-heading">
                    {definition.label}
                  </span>
                  <span className="text-[11px] text-contrast/50">
                    {definition.description}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          );
        })}
      </CommandList>
    </Command>
  );
}
